import React, { useEffect, useState } from 'react';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { useGoogleSearchContext } from './GoogleInstantSearch';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { toCloudRetailProductDetail } from '@jbhi-fi/utils/src/ga4Tracking/functions/toCloudRetailProductDetail';
import { GIFTCARDSKU } from '../../utils/gift-card.js';
import { styles as featuredStyles } from '../sponsoredcontent/SponseredContentResults.css';

const GoogleFeaturedProducts = () => {
  const { featuredProducts, attributionToken } = useGoogleSearchContext();
  const isDesktop = useBreakpoint('lg', 'up');
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const [featuredTracked, setFeaturedTracked] = useState(false);

  useEffect(() => {
    if (featuredProducts.length && !featuredTracked) {
      trackViewItemList({
        currency: getStoreCurrency(),
        listName: 'Collection - Hero Content',
        viewedItems: transformAlgoliaHitForTracking(featuredProducts),
        cloudRetail: {
          attributionToken,
          productDetails: featuredProducts.map(toCloudRetailProductDetail),
          filter: null,
        },
      });
      setFeaturedTracked(true);
    }
  }, [featuredProducts, featuredTracked]);

  if (!featuredProducts.length) return null;

  return (
    <div className={featuredStyles.heroContentContainer}>
      <div className={featuredStyles.heroContentInnerContainer}>
        <DomFont as="h3" className={featuredStyles.heroContentTitle}>
          Featured
        </DomFont>
        <div className={featuredStyles.heroContentHits}>
          {isDesktop ? (
            <div className={featuredStyles.productCardContainer}>
              {featuredProducts.map((hit, i) => (
                <ProductCard
                  {...AlgoliaHitTransformer.toProductCardProps(hit, {
                    trackingDescriptor: {
                      page: 'collection',
                      group: 'heroContent',
                      currency: getStoreCurrency(),
                      ecommerceExtraProps: { list: 'collection', position: i + 1 },
                    },
                  })}
                  {...productCartPropsOverrides}
                  key={`${hit.variant_id} ${i}`}
                  shouldHideSwatches
                  variant="tablet"
                  getIsCompared={isAddedToCompare}
                  onCompare={compareHandleClick}
                  getShouldShowCompare={shouldShowCompare}
                  isLoggedIn={isLoggedIn}
                  shouldHideCartOverlay={productCartPropsOverrides.shouldHideCartOverlay || GIFTCARDSKU === hit.sku}
                  onUpdateWishlist={handleUpdateWishlist}
                  getIsInWishlist={getIsInWishlist}
                  onAddToCart={
                    onAddToCart
                      ? async (e: any, _sku: any, variantId: any, eventData: any) => {
                          const isSuccess = await onAddToCart(e, hit, variantId, eventData?.trackingDescriptor);
                          return isSuccess;
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          ) : (
            <ProductCarousel
              fullBleed={false}
              fullHeightItems
              products={featuredProducts}
              renderProduct={({ hit, position }) => (
                <ProductCard
                  {...AlgoliaHitTransformer.toProductCardProps(hit, {
                    trackingDescriptor: {
                      page: 'collection',
                      group: 'heroContent',
                      currency: getStoreCurrency(),
                      ecommerceExtraProps: { list: 'collection - heroTile', position },
                    },
                  })}
                  {...productCartPropsOverrides}
                  shouldHideSwatches
                  variant="tablet"
                  getIsCompared={isAddedToCompare}
                  onCompare={compareHandleClick}
                  getShouldShowCompare={shouldShowCompare}
                  isLoggedIn={isLoggedIn}
                  onUpdateWishlist={handleUpdateWishlist}
                  getIsInWishlist={getIsInWishlist}
                  onAddToCart={
                    onAddToCart
                      ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                      : undefined
                  }
                />
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleFeaturedProducts;
